import { createTheme } from '@mui/material/styles'

export const spaceColors = {
    coral: '#ee7752',
    violet: '#e73c7e',
    blue: '#23a6d5',
    green: '#23d5ab',
}

export interface IColor {
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
    1000: string
}

export const indigo: IColor = {
    100: '#EEF2FF',
    200: '#E0E7FF',
    300: '#C7D2FE',
    400: '#A5B4FC',
    500: '#818CF8',
    600: '#6366F1',
    700: '#4F46E5',
    800: '#4338CA',
    900: '#3730A3',
    1000: '#312E81',
}

export const violet: IColor = {
    100: '#F5F3FF',
    200: '#ECE9FE',
    300: '#DDD6FE',
    400: '#C3B5FD',
    500: '#A48AFB',
    600: '#875BF7',
    700: '#7839EE',
    800: '#6927DA',
    900: '#5720B7',
    1000: '#491C96',
}

export const purple: IColor = {
    100: '#F4F3FF',
    200: '#EBE9FE',
    300: '#D9D6FE',
    400: '#BDB4FE',
    500: '#9B8AFB',
    600: '#7A5AF8',
    700: '#6938EF',
    800: '#5925DC',
    900: '#4A1FB8',
    1000: '#3E1C96',
}

export const fuchsia: IColor = {
    100: '#FDF4FF',
    200: '#FBE8FF',
    300: '#F6D0FE',
    400: '#EEAAFD',
    500: '#E478FA',
    600: '#D444F1',
    700: '#BA24D5',
    800: '#9F1AB1',
    900: '#821890',
    1000: '#6F1877',
}

export const pink: IColor = {
    100: '#FDF2FA',
    200: '#FCE7F6',
    300: '#FCCEEE',
    400: '#FAA7E0',
    500: '#F670C7',
    600: '#EE46BC',
    700: '#DD2590',
    800: '#C11574',
    900: '#9E165F',
    1000: '#851651',
}

export const rose: IColor = {
    100: '#FFF1F3',
    200: '#FFE4E8',
    300: '#FECDD6',
    400: '#FEA3B4',
    500: '#FD6F8E',
    600: '#F63D68',
    700: '#E31B54',
    800: '#C01048',
    900: '#A11043',
    1000: '#89123E',
}

export const teal: IColor = {
    100: '#F0FDF9',
    200: '#CCFBEF',
    300: '#99F6E0',
    400: '#5FE9D0',
    500: '#2ED3B7',
    600: '#15B79E',
    700: '#0E9384',
    800: '#107569',
    900: '#125D56',
    1000: '#134E48',
}

export const cyan: IColor = {
    100: '#ECFDFF',
    200: '#CFF9FE',
    300: '#A5F0FC',
    400: '#67E3F9',
    500: '#22CCEE',
    600: '#06AED4',
    700: '#088AB2',
    800: '#0E7090',
    900: '#155B75',
    1000: '#164C63',
}

// also known as light blue
export const sky: IColor = {
    100: '#F0F9FF',
    200: '#E0F2FE',
    300: '#B9E6FE',
    400: '#7CD4FD',
    500: '#36BFFA',
    600: '#0BA5EC',
    700: '#0086C9',
    800: '#026AA2',
    900: '#065986',
    1000: '#0B4A6F',
}

export const blue: IColor = {
    100: '#EFF4FF',
    200: '#D1E0FF',
    300: '#B2CCFF',
    400: '#84ADFF',
    500: '#528BFF',
    600: '#2970FF',
    700: '#155EEF',
    800: '#004EEB',
    900: '#0040C1',
    1000: '#00359E',
}

//palette for warning
export const amber: IColor = {
    100: '#FFFAEB',
    200: '#FEF0C7',
    300: 'rgba(247, 144, 9, 0.3)',
    400: '#FEC84B',
    500: '#FDB022',
    600: '#F79009',
    700: '#DC6803',
    800: '#B54708',
    900: '#93370D',
    1000: '#7A2E0E',
}

export const yellow: IColor = {
    100: '#FEFBE8',
    200: '#FEF7C3',
    300: '#FEEE95',
    400: '#FDE272',
    500: '#FAC515',
    600: '#EAAA08',
    700: '#CA8504',
    800: '#A15C07',
    900: '#854A0E',
    1000: '#713B12',
}

export const lime: IColor = {
    100: '#F5FBEE',
    200: '#E6F4D7',
    300: '#CEEAB0',
    400: '#ACDC79',
    500: '#86CB3C',
    600: '#669F2A',
    700: '#4F7A21',
    800: '#3F621A',
    900: '#335015',
    1000: '#2B4212',
}

//palette for success colors
export const green: IColor = {
    100: '#ECFDF3',
    200: '#D1FADF',
    300: '#A6F4C5',
    400: '#6CE9A6',
    500: '#32D583',
    600: '#12B76A',
    700: '#039855',
    800: '#027A48',
    900: '#05603A',
    1000: '#054F31',
}

export const emerald: IColor = {
    100: '#ECFDF5',
    200: '#D1FAE5',
    300: '#A7F3D0',
    400: '#6EE7B7',
    500: '#34D399',
    600: '#10B981',
    700: '#059669',
    800: '#047857',
    900: '#065F46',
    1000: '#064E3B',
}

export const gray: IColor = {
    100: '#F9FAFB',
    200: '#F3F4F6',
    300: '#E5E7EB',
    400: '#D2D6DB',
    500: '#9DA4AE',
    600: '#6C737F',
    700: '#4D5761',
    800: '#384250',
    900: '#1F2A37',
    1000: '#111927',
}

export const warmGray: IColor = {
    100: '#FAFAF9',
    200: '#F5F5F4',
    300: '#E7E5E4',
    400: '#D7D3D0',
    500: '#A9A29D',
    600: '#79716B',
    700: '#57534E',
    800: '#44403C',
    900: '#292524',
    1000: '#1C1917',
}

//palette for error colors
export const red: IColor = {
    100: '#FEF3F2',
    200: '#FEE4E2',
    300: '#FECDCA',
    400: '#FDA29B',
    500: '#F97066',
    600: '#F04438',
    700: '#D92D20',
    800: '#B42318',
    900: '#912018',
    1000: '#7A271A',
}

export const orange: IColor = {
    100: '#FFF4ED',
    200: '#FFE6D5',
    300: '#FFD6AE',
    400: '#FF9C66',
    500: '#FF692E',
    600: '#FF4405',
    700: '#E62E05',
    800: '#BC1B06',
    900: '#97180C',
    1000: '#771A0D',
}

//palette for neutral colors
export const blueGray: IColor = {
    100: '#F9FAFB',
    200: '#F2F4F7',
    300: '#EAECF0',
    400: '#D0D5DD',
    500: '#98A2B3',
    600: '#667085',
    700: '#475467',
    800: '#344054',
    900: '#1D2939',
    1000: '#101828',
}

export const coolGray: IColor = {
    100: '#F9F9FB',
    200: '#EFF1F5',
    300: '#DCDFEA',
    400: '#B9C0D4',
    500: '#7D89B0',
    600: '#5D6B98',
    700: '#4A5578',
    800: '#404968',
    900: '#30374F',
    1000: '#111322',
}

export const allColors = {
    amber,
    blue,
    blueGray,
    coolGray,
    cyan,
    emerald,
    fuchsia,
    gray,
    green,
    indigo,
    lime,
    orange,
    pink,
    purple,
    red,
    rose,
    sky,
    teal,
    violet,
    warmGray,
    yellow,
}

// Copenhagen Suborbitals Theme
const federal_blue = {
    DEFAULT: '#0b0b44',
    100: '#02020d',
    200: '#04041a',
    300: '#060627',
    400: '#090935',
    500: '#0b0b44',
    600: '#17178c',
    700: '#2323d7',
    800: '#6a6ae7',
    900: '#b4b4f3',
}
export const french_gray = {
    DEFAULT: '#c7c8d0',
    100: '#25262c',
    200: '#4a4c59',
    300: '#6f7285',
    400: '#9b9dab',
    500: '#c7c8d0',
    600: '#d3d3da',
    700: '#dedee3',
    800: '#e9e9ec',
    900: '#f4f4f6',
}
export const giants_orange = {
    DEFAULT: '#f1642b',
    100: '#361203',
    200: '#6b2307',
    300: '#a1350a',
    400: '#d7470e',
    500: '#f1642b',
    600: '#f48356',
    700: '#f7a280',
    800: '#fac1ab',
    900: '#fce0d5',
}

export const darkTheme = createTheme({
    palette: {
        primary: {
            main: federal_blue.DEFAULT,
        },
        secondary: {
            main: '#c1c6c9',
        },
        background: {
            default: '#ffffff',
            paper: '#f0f0f0',
        },
    },
})
